import type { ResponseError, Risk } from '@rtkwlf/mr-infra-risk';
import { type GetRisksRequest } from '@rtkwlf/mr-infra-risk';
import {
  useInfiniteQuery,
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query';

import { useOrganizationContext } from '@packages/legacy/organization-context';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';

import { useRiskServiceApi } from '../use-mr-infra-risk-api';

export type GetRisksParams = Omit<GetRisksRequest, 'orgUUID'>;

export const getRisksQueryKey = ({ orgUUID, ...params }: GetRisksRequest) => {
  return [orgUUID, 'mr-infra-risk', 'risks', params];
};

type risksResponse = {
  data: Array<Risk>;
  metaData: {
    cursor: string;
    total: number;
    totalFiltered: number;
  };
};

export const useRisks = (
  params: GetRisksParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      risksResponse,
      ResponseError,
      InfiniteData<risksResponse, string>,
      risksResponse,
      QueryKey,
      string
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const [testDataFF] = useFeatureFlag('RISK_IN_UP_LIVE_TEST_DATA');
  const api = useRiskServiceApi();
  const { id } = useOrganizationContext().selectedOrganization;

  const requestParams: GetRisksRequest = {
    orgUUID: testDataFF ? 'c8923d83-d419-4453-b768-3e1fbd6d16c2' : id,
    ...params,
  };

  return useInfiniteQuery({
    queryFn: async ({ pageParam }: { pageParam: string }) => {
      if (pageParam !== '') requestParams.cursor = pageParam;
      const response = await api.getRisksRaw(requestParams);
      const risks = await response.value();
      const riskResponse: risksResponse = {
        data: risks,
        metaData: {
          cursor: response.raw.headers.get('x-cursor-next') || '',
          total: 0, // Assuming the API returns total count in the future
          totalFiltered: 0,
        },
      };
      return riskResponse;
    },
    queryKey: getRisksQueryKey(requestParams),
    initialPageParam: '',
    getNextPageParam: (lastPage, _allPages) => {
      return lastPage.metaData.cursor;
    },
    ...options,
  });
};
